import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

const API_URL = process.env.REACT_APP_API_URL || '';

// Atlas público (topojson), carregado do CDN em tempo de execução — sem precisar
// empacotar o arquivo no repo.
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

function countryNameOf(geo) {
  const p = geo.properties || {};
  return p.name || p.NAME || p.NAME_LONG || p.ADMIN || '';
}

/**
 * Mapa-múndi por país. `data` = [{country, count}] (nome em inglês, igual ao atlas).
 * Ao passar o mouse num país, o painel ao lado mostra a contagem e as matérias
 * (com link). O painel mantém o último país visitado para dar tempo de clicar.
 */
function WorldMap({ data, theme }) {
  const [hover, setHover] = useState(null);
  const [articles, setArticles] = useState({});
  const timer = useRef(null);

  const countryCount = useMemo(() => {
    const map = {};
    for (const row of data || []) map[row.country] = row.count;
    return map;
  }, [data]);

  const max = Math.max(1, ...Object.values(countryCount));

  const colorFor = (name) => {
    const count = countryCount[name];
    if (!count) return '#e2e8f0';
    const intensity = 0.3 + 0.7 * (count / max);
    return `rgba(79, 70, 229, ${intensity.toFixed(2)})`;
  };

  const cacheKey = (name) => `${theme || ''}|${name}`;

  useEffect(() => {
    setArticles({});
  }, [theme, data]);

  const onEnter = (name) => {
    setHover(name);
    clearTimeout(timer.current);
    if (!countryCount[name] || articles[cacheKey(name)]) return;
    timer.current = setTimeout(async () => {
      try {
        const params = { country: name, limit: 6 };
        if (theme) params.theme = theme;
        const res = await axios.get(`${API_URL}/api/news`, { params });
        setArticles((prev) => ({ ...prev, [cacheKey(name)]: res.data.news || [] }));
      } catch (err) {
        console.error(err);
      }
    }, 150);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  const list = hover ? articles[cacheKey(hover)] : null;
  const count = hover ? countryCount[hover] || 0 : 0;
  const filterQs = (name) =>
    `?country=${encodeURIComponent(name)}${theme ? `&theme=${encodeURIComponent(theme)}` : ''}`;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <ComposableMap projectionConfig={{ scale: 135 }} style={{ width: '100%', height: 'auto' }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = countryNameOf(geo);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={hover === name ? '#4338ca' : colorFor(name)}
                    stroke="#ffffff"
                    strokeWidth={0.5}
                    onMouseEnter={() => onEnter(name)}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', cursor: 'pointer' },
                      pressed: { outline: 'none' },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      <div className="min-h-[8rem] border border-slate-200 p-4 text-sm">
        {!hover && <p className="text-slate-400">Passe o mouse sobre um país para ver as notícias.</p>}
        {hover && (
          <>
            <h3 className="font-semibold text-slate-900">{hover}</h3>
            <p className="mb-3 text-xs text-slate-500">
              {count > 0 ? `${count} notícia(s) no período` : 'Sem notícias no período'}
            </p>
            {count > 0 && !list && <p className="text-xs text-slate-400">Carregando…</p>}
            {list && (
              <ul className="space-y-2">
                {list.map((a) => (
                  <li key={a.id}>
                    <Link to={`/news/${a.id}`} className="line-clamp-2 text-slate-700 hover:text-brand-600 hover:underline">
                      {a.title_pt || a.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {count > 0 && (
              <Link to={`/news${filterQs(hover)}`} className="mt-3 inline-block text-xs font-medium text-brand-600 hover:underline">
                Ver todas as {count} →
              </Link>
            )}
          </>
        )}
      </div>

      <p className="text-xs text-slate-400 lg:col-span-3">
        País identificado pela classificação de cada notícia; notícias globais ou ainda sem país não aparecem no mapa.
      </p>
    </div>
  );
}

export default WorldMap;
